import { trackOutputAction } from 'analytics';
import forge from 'apis/forge';
import { putTextOnClipboard } from 'clipboard';
import Icon, { IconType } from 'components/Icon';
import { DesignSystem } from 'dragonmouse-shared/interfaces';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { selectIsMobile } from 'redux/mode/reducers';
import { selectUser } from 'redux/db/reducers';
import { RootReducer } from 'redux/reducers';
import { shallowEqual } from 'react-redux';
import { unthemed } from 'tokens';
import { designSystemShareUrl } from 'utils';
import Modal from '../..';
import UserAccessManager from '../UserAccessManager';
import * as S from '../styles';

interface ShareDesignSystemModalProps {
	designSystem: DesignSystem;
	onClose: () => void;
}

const ShareDesignSystemModal = ({ designSystem, onClose }: ShareDesignSystemModalProps) => {
	const { user, isMobile } = useSelector((r: RootReducer) => {
		return {
			user: selectUser(r.db),
			isMobile: selectIsMobile(r.mode),
		};
	}, shallowEqual);

	const canNativeShare =
		isMobile && typeof navigator !== 'undefined' && typeof navigator.share === 'function';

	//------------------------------------------------------------------------------------------------
	// HOOKS
	//------------------------------------------------------------------------------------------------
	const close = useCallback(() => {
		onClose();
	}, [onClose]);

	return (
		<Modal
			title={'Share this design system'}
			cancelLabel="Close"
			submitLabel="Save"
			onClose={close}
		>
			<>
				<UserAccessManager
					entityId={designSystem.id}
					getUsers={async (id: string) => {
						return await forge.getDesignSystemUsers({ designSystemId: id });
					}}
					grantAccess={async (id: string, emails: string[]) => {
						await forge.grantDesignSystemAccess.withToast()({ designSystemId: id, emails });
					}}
					revokeAccess={async (id: string, email: string) => {
						await forge.revokeDesignSystemAccess.withToast()({ designSystemId: id, email });
					}}
					onAccessGranted={() => {
						// Update user metadata for analytics
						if (user?.id) {
							forge.updateUser({
								update: {
									id: user.id,
									metadata: {
										lastSharedDate: new Date().toISOString(),
									},
								},
							});
						}
					}}
					errorMessages={{
						fetchFailed: 'Failed to fetch design system users',
						revokeFailed: 'Failed to revoke user access',
					}}
				/>
				<S.LinkSection>
					<S.LinkButton
						onClick={async () => {
							const url = designSystemShareUrl(designSystem);

							if (canNativeShare) {
								try {
									await navigator.share({
										title: designSystem.title ?? 'Design system',
										url,
									});
									trackOutputAction('share_design_system_link');
									return;
								} catch (error) {
									if ((error as DOMException)?.name === 'AbortError') {
										return;
									}
									console.warn('Native share failed, falling back to copy', error);
								}
							}

							try {
								await putTextOnClipboard(url);
								trackOutputAction('copy_design_system_link');
								toast.success('Link copied');
							} catch (error) {
								toast.error('Copy failed — please copy manually');
								console.error('Failed to copy design system link to clipboard', error);
							}
						}}
					>
						<Icon
							type={canNativeShare ? IconType.SHARE : IconType.COPY}
							color={unthemed.gray['0']}
							width={14}
							height={14}
						/>
						<S.Link>
							{canNativeShare ? 'Share design system link' : 'Copy design system link'}
						</S.Link>
					</S.LinkButton>
				</S.LinkSection>
			</>
		</Modal>
	);
};

export default ShareDesignSystemModal;
